import type { User } from '@/modules/user/types/user.type';

export const PERMISSIONS = {
  SYSTEM_ADMIN: 'SYSTEM_ADMIN',

  VIEW_USERS: 'VIEW_USERS',
  CREATE_USERS: 'CREATE_USERS',
  UPDATE_USERS: 'UPDATE_USERS',
  DELETE_USERS: 'DELETE_USERS',

  VIEW_DEVICES: 'VIEW_DEVICES',
  CREATE_DEVICES: 'CREATE_DEVICES',
  UPDATE_DEVICES: 'UPDATE_DEVICES',
  DELETE_DEVICES: 'DELETE_DEVICES',
  CONTROL_DEVICES: 'CONTROL_DEVICES',
  VIEW_BROADCAST_CONTENTS: 'VIEW_BROADCAST_CONTENTS',
  MANAGE_BROADCAST_CONTENTS: 'MANAGE_BROADCAST_CONTENTS',
  VIEW_BROADCAST_SCHEDULES: 'VIEW_BROADCAST_SCHEDULES',
  MANAGE_BROADCAST_SCHEDULES: 'MANAGE_BROADCAST_SCHEDULES',
  CONTROL_BROADCAST_SCHEDULES: 'CONTROL_BROADCAST_SCHEDULES',
  PLAY_CALL_RECORDINGS: 'PLAY_CALL_RECORDINGS',
  CREATE_SIP_CALLS: 'CREATE_SIP_CALLS',
  VIEW_SIP_CALLS: 'VIEW_SIP_CALLS',
  CANCEL_SIP_CALLS: 'CANCEL_SIP_CALLS',
  CREATE_ANNOUNCEMENTS: 'CREATE_ANNOUNCEMENTS',
  VIEW_ANNOUNCEMENTS: 'VIEW_ANNOUNCEMENTS',
  CANCEL_ANNOUNCEMENTS: 'CANCEL_ANNOUNCEMENTS',

  VIEW_DEVICE_GROUPS: 'VIEW_DEVICE_GROUPS',
  MANAGE_DEVICE_GROUPS: 'MANAGE_DEVICE_GROUPS',

  VIEW_REPORTS: 'VIEW_REPORTS',
  VIEW_MONITORING: 'VIEW_MONITORING',

  VIEW_ORGANIZATIONS: 'VIEW_ORGANIZATIONS',
  CREATE_ORGANIZATIONS: 'CREATE_ORGANIZATIONS',
  UPDATE_ORGANIZATIONS: 'UPDATE_ORGANIZATIONS',
  DELETE_ORGANIZATIONS: 'DELETE_ORGANIZATIONS',

  VIEW_PERIPHERALS: 'VIEW_PERIPHERALS',

  VIEW_ROLES: 'VIEW_ROLES',
  CREATE_ROLES: 'CREATE_ROLES',
  UPDATE_ROLES: 'UPDATE_ROLES',
  DELETE_ROLES: 'DELETE_ROLES',

  SYSTEM_CONFIGURATION: 'SYSTEM_CONFIGURATION',
} as const;

export type PermissionCode = typeof PERMISSIONS[keyof typeof PERMISSIONS];

const normalizePermissionCode = (value: unknown): string =>
  String(value ?? '').trim().toUpperCase();

export const getPermissionCodes = (user: User | null | undefined): string[] => {
  if (!user) return [];

  const rolePermissions = Array.isArray(user.role?.permissions)
    ? user.role.permissions.map(item => normalizePermissionCode(item?.code))
    : [];

  const directPermissions = Array.isArray(user.permissions)
    ? user.permissions.map(item => normalizePermissionCode(item?.code))
    : [];

  const legacyPermissions = Array.isArray(user.userPermission)
    ? user.userPermission
        .map(item => normalizePermissionCode(item?.permission?.code))
        .filter(Boolean)
    : [];

  return [...new Set([...rolePermissions, ...directPermissions, ...legacyPermissions].filter(Boolean))];
};

export const hasPermission = (
  user: User | null | undefined,
  permission?: string | string[],
): boolean => {
  if (!permission || (Array.isArray(permission) && permission.length === 0)) {
    return true;
  }

  const granted = new Set(getPermissionCodes(user));

  // Chỉ cấp toàn quyền khi BE thực sự gán SYSTEM_ADMIN.
  if (granted.has(PERMISSIONS.SYSTEM_ADMIN)) return true;

  const required = (Array.isArray(permission) ? permission : [permission])
    .map(normalizePermissionCode)
    .filter(Boolean);

  return required.some(code => granted.has(code));
};

export const getDefaultAuthorizedRoute = (user: User | null | undefined): string => {
  const candidates: Array<[string, string]> = [
    [PERMISSIONS.VIEW_MONITORING, '/monitoring'],
    [PERMISSIONS.VIEW_DEVICES, '/devices'],
    [PERMISSIONS.VIEW_DEVICE_GROUPS, '/device-groups'],
    [PERMISSIONS.VIEW_PERIPHERALS, '/peripherals'],
    [PERMISSIONS.VIEW_USERS, '/users'],
    [PERMISSIONS.VIEW_ROLES, '/roles'],
    [PERMISSIONS.VIEW_ORGANIZATIONS, '/organizations'],
  ];

  return candidates.find(([permission]) => hasPermission(user, permission))?.[1] ?? '/account-admin';
};
